import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../auth-lib';
import { Button, Modal } from '../../design-system';
import '../styles/TaskCompletionFlow.css';

// Completing a task, shown as the several things it actually is.
//
// A task closing is not a field edit — it files the compliance record, releases
// whatever was waiting on it, and turns its charges into a billable draft. This
// walks through those and says what each one did, because "Saved ✓" for an
// operation with five consequences tells nobody whether the invoice appeared.
//
// ⚠️ THE WORK IS ONE SERVER CALL, NOT ONE PER ROW. It has to be: the steps roll
// back together if any of them fails (utils/taskCompletion.js), and rollback
// cannot be orchestrated from a browser that might be closed halfway. The rows
// reveal in sequence purely so the outcomes are readable as they land — every
// status shown is the real one the server returned, and the reveal is capped so
// a five-step result never takes longer to read than it did to run.
//
// ⚠️ THE FLOW NEVER ISSUES OR SENDS THE INVOICE. It drafts one. Putting a bill
// in front of a client decides between a proforma and a tax invoice — two
// different statutory series with different GST consequences — which is not a
// decision to bury inside a status change. The last panel offers the draft and
// the billing address; the send happens on the invoice, where that choice lives.

// How long each row waits before revealing the next. Short enough that the
// whole thing reads as one action rather than a progress bar being sat through.
const REVEAL_MS = 260;

const ICON = {
  done: '✓',
  skipped: '–',
  warning: '!',
  failed: '✕',
  rolled_back: '↩',
  running: '',
  pending: '',
};

function StepRow({ step, state }) {
  return (
    <li className={`tcf-step is-${state}`}>
      <span className="tcf-step-mark" aria-hidden>
        {state === 'running' ? <span className="tcf-spin" /> : ICON[step.status] || ''}
      </span>
      <span className="tcf-step-body">
        <span className="tcf-step-label">{step.label}</span>
        {state !== 'running' && step.detail && (
          <span className="tcf-step-detail">{step.detail}</span>
        )}
      </span>
    </li>
  );
}

export default function TaskCompletionFlow({ task, onClose, onDone }) {
  const navigate = useNavigate();
  const [steps, setSteps] = useState([]);
  const [revealed, setRevealed] = useState(0);
  const [outcome, setOutcome] = useState(null); // null | { ok, invoice, billing, ... }
  const [error, setError] = useState('');
  const timers = useRef([]);

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };

  const run = useCallback(async () => {
    clearTimers();
    setSteps([]);
    setRevealed(0);
    setOutcome(null);
    setError('');

    let data;
    try {
      const res = await apiClient.post(`/api/tasks/${task._id}/complete`);
      data = res.data;
    } catch (e) {
      // A 409 is a refusal WITH per-step detail — the task was blocked, or a
      // step failed and everything was rolled back. Render it as the flow
      // rather than as a toast, because which step broke is the useful part.
      data = e.response?.status === 409 ? e.response.data : null;
      if (!data) {
        setError(e.response?.data?.error || 'Could not complete the task');
        return;
      }
    }

    setSteps(data.steps || []);
    setOutcome(data);
    // Reveal one row at a time. Purely presentational — the work is already done.
    (data.steps || []).forEach((_, i) => {
      timers.current.push(setTimeout(() => setRevealed(i + 1), REVEAL_MS * (i + 1)));
    });
  }, [task._id]);

  useEffect(() => {
    run();
    return clearTimers;
  }, [run]);

  const finished = outcome && revealed >= (steps.length || 0);
  const failed = outcome && outcome.ok === false;

  // Nothing here is undone by closing — the server already committed or rolled
  // back — but leaving mid-reveal shouldn't strand the parent's stale copy.
  const close = () => { clearTimers(); onClose(); if (outcome?.ok) onDone?.(outcome); };

  return (
    <Modal
      isOpen
      onClose={close}
      title={failed ? 'Could not complete this task' : 'Completing this task'}
      size="md"
      footer={(
        <div className="tcf-actions">
          {finished && failed && (
            <Button variant="secondary" onClick={run}>Try again</Button>
          )}
          {finished && !failed && outcome.invoice && (
            <Button
              variant="secondary"
              onClick={() => {
                close();
                navigate(`/org-admin/finance?tab=invoices&invoice=${outcome.invoice.id}`);
              }}
            >
              Review &amp; send invoice
            </Button>
          )}
          <Button variant="primary" onClick={close} disabled={!finished && !error}>
            {finished || error ? 'Close' : 'Working…'}
          </Button>
        </div>
      )}
    >
      <div className="tcf">
        <p className="tcf-task">{task.title}</p>

        {error ? (
          <p className="tcf-error" role="alert">{error}</p>
        ) : (
          <ul className="tcf-steps">
            {steps.map((s, i) => (
              <StepRow
                key={s.key}
                step={s}
                state={i < revealed ? s.status : (i === revealed ? 'running' : 'pending')}
              />
            ))}
            {!steps.length && (
              <li className="tcf-step is-running">
                <span className="tcf-step-mark"><span className="tcf-spin" /></span>
                <span className="tcf-step-body"><span className="tcf-step-label">Working…</span></span>
              </li>
            )}
          </ul>
        )}

        {finished && failed && (
          <div className="tcf-panel is-bad">
            <b>Nothing was changed.</b>
            <span>
              Every step that had already run was undone, so the task is exactly as it was.
              Fix what the failing step reports and try again.
            </span>
          </div>
        )}

        {finished && !failed && (
          <div className="tcf-panel is-good">
            <b>Done.</b>
            <span>
              {outcome.invoice
                ? 'The invoice is a draft — nothing has gone to the client yet.'
                : 'The task is closed and everything downstream is up to date.'}
            </span>
          </div>
        )}

        {/* The draft, and the one next decision worth offering. Deliberately a
            LINK to the invoice rather than a Send button: choosing proforma vs
            tax invoice belongs on the invoice, not in this modal. */}
        {finished && !failed && outcome.invoice && (
          <div className="tcf-invoice">
            <div className="tcf-invoice-head">
              <span className="tcf-invoice-label">Draft invoice</span>
              <span className="tcf-invoice-total">
                ₹{Number(outcome.invoice.total || 0).toLocaleString('en-IN')}
              </span>
            </div>
            <span className="tcf-invoice-client">
              {outcome.invoice.clientName} · {outcome.invoice.lineCount} line
              {outcome.invoice.lineCount === 1 ? '' : 's'}
            </span>
            {outcome.billing?.recipients?.length ? (
              <span className="tcf-invoice-to">
                Will go to {outcome.billing.recipients.join(', ')} when you send it
              </span>
            ) : (
              <span className="tcf-invoice-to is-warn">
                No billing email on file for this client — add one before sending.
              </span>
            )}
          </div>
        )}

      </div>
    </Modal>
  );
}
